import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Leaf } from "lucide-react";

const LINKS = [
  { label: "Who We Are", href: "#about" },
  { label: "Feed", href: "#feed" },
  { label: "Spices", href: "#spices" },
  { label: "Tea", href: "#tea" },
  { label: "Founders", href: "#founders" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#F0E6C0]/90 backdrop-blur-md border-b border-[#DBCFA0] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#3D2817] text-[#3D2817] transition-colors duration-300 group-hover:bg-[#3D2817] group-hover:text-[#F0E6C0]">
            <Leaf size={20} strokeWidth={1.5} />
          </span>
          <span className="font-display text-2xl leading-none tracking-tight text-[#3D2817]">
            Agro Trade Global
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="link-underline text-sm uppercase tracking-[0.15em] text-[#3D2817]/80 hover:text-[#3D2817]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-cta-quote"
            className="rounded-full bg-[#3D2817] px-6 py-2.5 text-sm uppercase tracking-[0.15em] text-[#F0E6C0] transition-transform duration-300 hover:scale-[0.96]"
          >
            Get a Quote
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#3D2817]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#F0E6C0] border-t border-[#DBCFA0]"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg text-[#3D2817] uppercase tracking-wide"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="rounded-full bg-[#3D2817] px-6 py-3 text-center text-sm uppercase tracking-[0.15em] text-[#F0E6C0]"
              >
                Get a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
